import {
  Box,
  Flex,
  IconButton,
  Input,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import {
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataLake, typeguards, Util } from '@finley-technologies/finley-types';

import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '../requests/requests.gql.generated';
import { AthenaDataPreviewModal } from './AthenaDataPreviewModal';
import { AthenaQueryExecuteModal } from './AthenaQueryExecuteModal';
import { BindingContainer } from './BindingContainer';
import { ColumnNamesContainer } from './ColumnNamesContainer';
import { inputWidth } from './consts';
import { LenderAthenaQueryView } from './LenderAthenaQueryView';
import { VariableContainer } from './VariableContainer';
import { AthenaQueryStringWithSchemaSidebarContainer } from 'packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import {
  AddIconButton,
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
  PasteIconButton,
  SaveIconButton,
  SearchIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteConfirmationModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import {
  AddQueryInputModal,
  PortfolioCovenantQueryInput,
} from 'packages/settings/ui/AddQueryInputModal';
import { PortfolioCovenantEditTab } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsSidebar';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

export const createAthenaQueryShell = (
  idx: number,
  name?: string,
): CreditAgreement.Config.AthenaConfig.Query => ({
  query: {
    name: name ?? `queryName${idx}`,
    queryString: 'SQL QUERY GOES HERE;',
  },
  variables: [],
  bindings: [],
  columnNames: ['placeholderColumn'],
});

type AthenaQueryViewProps = {
  organizationId: string;
  queryView: CreditAgreement.Config.AthenaConfig.Query;
  existingQueries: CreditAgreement.Config.AthenaConfig.Query[];
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
  setSelectedQueryInput?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >; // used to navigate to a new query input, for example when a query is duplicated
  setSelectedTab?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >; // used to navigate to another tab when the currently viewed input has been deleted
  selectedInputDate?: Util.FullDateStr;
};
export const AthenaQueryView = ({
  organizationId,
  queryView,
  existingQueries,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
  inputDateType,
  presetVariables,
  validationVariables,
  variableSubTitle,
  setSelectedQueryInput,
  setSelectedTab,
  selectedInputDate,
}: AthenaQueryViewProps) => {
  const toast = useToast();
  const {
    onOpen: onDuplicateQueryOpen,
    isOpen: isDuplicateQueryOpen,
    onClose: onDuplicateQueryClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();
  const {
    isOpen: isPreviewQueryResultsDrawerOpen,
    onOpen: onPreviewQueryResultsDrawerOpen,
    onClose: onPreviewQueryResultsDrawerClose,
  } = useDisclosure();
  const queryStringEditModeDisclosure = useDisclosure();

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);
  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState<boolean>(false);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [queryStringState, setQueryStringState] = useState<string>(
    queryView.query.queryString,
  );
  const [
    isQueryStringSaveLoading,
    setIsQueryStringSaveLoading,
  ] = useState<boolean>(false);

  const duplicateQuery = useCallback(
    async ({ queryKeyInput }: { queryKeyInput: string }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const wipQueries = getIn(wipConfig.formProps.values, basePropertyKey);
      finleyAssert(
        typeguards.creditAgreement.isAthenaConfigQueries(wipQueries),
        `Retrieved non-Athena Queries structure for [key:${basePropertyKey}]`,
      );

      const copiedQuery = _.cloneDeep(queryView);
      copiedQuery.query.name = queryKeyInput;

      const wipQueriesCopy = _.cloneDeep(wipQueries);
      wipQueriesCopy.splice(idx + 1, 0, copiedQuery);

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: basePropertyKey,
        updatedProperty: wipQueriesCopy,
        setIsSaveLoading: setIsDuplicateLoading,
      });

      if (setSelectedQueryInput) {
        setSelectedQueryInput({
          queryInputType: 'singleQuery',
          queryInputKey: queryKeyInput,
        });
      }
    },
    [basePropertyKey, idx, queryView, wipConfig, setSelectedQueryInput],
  );

  const deleteQuery = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const wipQueries = getIn(wipConfig.formProps.values, basePropertyKey);
    finleyAssert(
      typeguards.creditAgreement.isAthenaConfigQueries(wipQueries),
      `Retrieved non-Athena Queries structure for [key:${basePropertyKey}]`,
    );

    const newQueriesAfterDelete = wipQueries.filter(
      (_unused, varIdx) => varIdx !== idx,
    );

    if (setSelectedTab && setSelectedQueryInput) {
      setSelectedTab('properties');
      setSelectedQueryInput(null);
    }

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: newQueriesAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [wipConfig, basePropertyKey, idx, setSelectedTab, setSelectedQueryInput]);

  const pasteQuery = useCallback(async () => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const wipQuery = getIn(wipConfig.formProps.values, fullPropertyKey);
    finleyAssert(
      typeguards.creditAgreement.isAthenaConfigQuery(wipQuery),
      `Retrieved non-Athena Query structure for [key:${fullPropertyKey}]`,
    );

    try {
      const clipboard = await navigator.clipboard.readText();
      const { query, bindings, variables: vars } = JSON.parse(clipboard);

      finleyAssert(
        typeof query === 'string' && query.length > 0,
        `query value must be a valid string, but got ${query}`,
      );
      finleyAssert(
        bindings,
        `bindings value must be a valid binding, but got ${bindings}`,
      );
      finleyAssert(
        typeguards.creditAgreement.isQueryVariables(vars),
        `variables value must be valid variable array, but got ${vars}`,
      );

      // If copying from playground, no need to include preset variables
      const variables = vars.filter(
        (v) => !presetVariables.some(({ name }) => name === v.name),
      );

      const queryCopy = _.cloneDeep(wipQuery);
      queryCopy.query.queryString = query;
      queryCopy.bindings = bindings;
      queryCopy.variables = variables;

      wipConfig.formProps.setFieldValue(fullPropertyKey, queryCopy);
    } catch (err) {
      const message =
        (commonTypeguards.isErrorType(err) && err.message) ||
        'Unable to paste query.';
      toast({
        ...errorToastConfig,
        id: errorToastId,
        title: message,
      });
    }
  }, [fullPropertyKey, presetVariables, toast, wipConfig]);

  const [isNameEditOpen, setNameEditOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>(queryView.query.name);
  const changeName = (): void => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const nameExists = existingQueries.some((q) => q.query.name === name);
    if (nameExists) {
      toast({
        ...errorToastConfig,
        title: 'Updating SQL Name Error',
        description: `"${name}" name is already in use`,
      });
      return;
    }

    const qv = _.cloneDeep(getIn(wipConfig.formProps.values, fullPropertyKey));
    qv.query.name = name;
    wipConfig.formProps.setFieldValue(fullPropertyKey, qv);

    setNameEditOpen(false);
  };

  const [
    executeAthenaQueryByRaw,
    { loading: isQueryLoading, variables: queryVariables },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      const { Items } = data.athenaQueryResponse;

      if (Items.length === 0 || Object.keys(Items[0]).length === 0) {
        toast({
          title: 'Athena Query Execute Update',
          description:
            'Query returned 0 data points. Query SQL and available column names have not been updated. Choose a different Trigger Date where data exists for the query and try again.',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });

        setIsQueryStringSaveLoading(false);
        return;
      }

      if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
        const queryCopy = _.cloneDeep(queryView);

        // save column names and query string in CA
        queryCopy.columnNames = Object.keys(Items[0]);
        if (queryVariables) {
          // save if the queryString has changed
          // so that we do not unnecessarily save when just previewing
          if (queryCopy.query.queryString !== queryVariables.query) {
            queryCopy.query.queryString = queryVariables.query;

            await wipConfig?.saveAndUpdateConfigCreditAgreementWip({
              wipConfig,
              propertyKey: fullPropertyKey,
              updatedProperty: queryCopy,
              setIsSaveLoading: setIsQueryStringSaveLoading,
            });
          }
        }

        setIsQueryStringSaveLoading(false);
        // set the query preview results and open preview drawer
        setQueryDataItems(Items);
        onPreviewQueryResultsDrawerOpen();
      }
    },
    onError: (error) => {
      setIsQueryStringSaveLoading(false);

      const errorDescription = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Incurred server error when executing Athena query'
        : error.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Query Execute Error',
        description: errorDescription,
      });
    },
  });

  const changeQueryString = useCallback(
    async ({
      newQueryString,
      queryInputDateForColumnUpdate,
    }: {
      newQueryString: string;
      queryInputDateForColumnUpdate: Util.FullDateStr;
    }): Promise<void> => {
      setIsQueryStringSaveLoading(true);

      if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
        return;
      }

      const qv = _.cloneDeep(
        getIn(wipConfig.formProps.values, fullPropertyKey),
      );
      qv.query.queryString = newQueryString;

      const errors = athenaValidate.validateAthenaQuery({
        query: qv.query.queryString,
        bindings: qv.bindings,
        variables: [
          ...validationVariables,
          ...presetVariables,
          ...qv.variables,
        ],
      });

      if (errors.length) {
        toast({
          ...errorToastConfig,
          title: 'Updating SQL Query Error',
          description: errors.join(', '),
        });
        throw errors;
      }

      const { variables, bindings } = queryView;

      const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
        {
          name: inputDateType,
          value: queryInputDateForColumnUpdate,
          parseNumber: true,
        },
      ];

      // If there's any overlap with provided "presetVariables", prioritize the
      // value that's set up with the modal through "supplementalVariables" and
      // filter out what already existed in "presetVariables"
      const updatedPresetVariables = presetVariables.filter((preset) => {
        return preset.name !== inputDateType;
      });

      executeAthenaQueryByRaw({
        variables: {
          organizationId,
          variables: [
            ...supplementalVariables,
            ...updatedPresetVariables,
            ...variables,
          ],
          bindings,
          query: newQueryString,
        },
      });
    },
    [
      organizationId,
      fullPropertyKey,
      wipConfig,
      presetVariables,
      toast,
      validationVariables,
      executeAthenaQueryByRaw,
      inputDateType,
      queryView,
    ],
  );

  const onClosePreviewQueryResultsDrawer = useCallback(() => {
    onPreviewQueryResultsDrawerClose();
    setQueryDataItems([]);
  }, [onPreviewQueryResultsDrawerClose]);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  if (isClientWipMode) {
    return (
      <LenderAthenaQueryView
        athenaQuery={queryView}
        fullPropertyKey={fullPropertyKey}
        wipConfig={wipConfig}
        duplicateParams={{
          isDuplicateQueryOpen,
          onDuplicateQueryOpen,
          onDuplicateQueryClose,
          duplicateQuery,
          isDuplicateLoading,
        }}
        deleteParams={{
          isDeleteConfirmationModalOpen,
          onDeleteConfirmationModalOpen,
          onDeleteConfirmationModalClose,
          deleteQuery,
          isDeleteLoading,
        }}
        changeQueryString={changeQueryString}
        isQueryStringSaveLoading={isQueryStringSaveLoading || isQueryLoading}
        inputDateType="triggerDate"
        presetVariables={presetVariables}
        validationVariables={validationVariables}
        selectedInputDate={selectedInputDate}
        queryDataItems={queryDataItems}
        isPreviewQueryResultsDrawerOpen={isPreviewQueryResultsDrawerOpen}
        onClosePreviewQueryResultsDrawer={onClosePreviewQueryResultsDrawer}
      />
    );
  }

  return (
    <Flex mb="2" p="3" flexDir="column" border="1px" borderColor="gray.700">
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text fontWeight="bold" mr="2">
          Query: &quot;{queryView.query.name}&quot;
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={() => setIsExecuteModalOpen(true)}
            />
            <PasteIconButton
              ariaLabel="paste query"
              label="Paste Query"
              onClick={() => pasteQuery()}
            />
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="singleQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>

      {isQueryOpen ? (
        <Box>
          {/* *** Query Name Management *** */}
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="4"
          >
            <Text width="40">Query Name:</Text>
            {isNameEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  type="text"
                  width={inputWidth}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeName()}
                />
                <EditIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setName(queryView.query.name);
                    setNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth}>{queryView.query.name}</Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setNameEditOpen(true)}
                    h="6"
                  />
                )}
              </>
            )}
          </Flex>

          {/* *** Variables Management *** */}
          <VariableContainer
            wipConfig={wipConfig}
            variables={queryView.variables}
            subTitle={variableSubTitle}
            variablePropertyKey={`${fullPropertyKey}.variables`}
          />

          {/* *** Bindings Management *** */}
          <BindingContainer
            wipConfig={wipConfig}
            bindings={queryView.bindings}
            bindingsPropertyKey={`${fullPropertyKey}.bindings`}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            query={queryView}
            queryType="singleQuery"
          />

          {/* *** SQL Query Management *** */}
          <AthenaQueryStringWithSchemaSidebarContainer
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}.queryString`}
            changeQueryString={changeQueryString}
            isQueryStringSaveLoading={isQueryStringSaveLoading}
            queryStringState={queryStringState}
            setQueryStringState={setQueryStringState}
            selectedInputDate={selectedInputDate}
            initialQueryString={queryView.query.queryString}
            editModeDisclosure={queryStringEditModeDisclosure}
          />

          {/* *** Column Name Portion *** */}
          <ColumnNamesContainer
            wipConfig={wipConfig}
            columnNames={queryView.columnNames}
            configPropertyKey={fullPropertyKey}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />
        </Box>
      ) : null}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <AthenaQueryExecuteModal
          organizationId={organizationId}
          wipConfig={wipConfig}
          athenaQuery={queryView}
          fullPropertyKey={fullPropertyKey}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          isOpen={isExecuteModalOpen}
          onCancel={() => setIsExecuteModalOpen(false)}
          onQuerySuccess={setQueryDataItems}
        />
      )}

      <AthenaDataPreviewModal
        queryDataItems={queryDataItems}
        onClose={() => setQueryDataItems([])}
      />
    </Flex>
  );
};

type AthenaQueriesViewProps = {
  organizationId: string;
  athenaQueries: CreditAgreement.Config.AthenaConfig.Query[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables?: CreditAgreement.Config.QueryVariable[];
  validationVariables?: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
};
export const AthenaQueriesView = ({
  organizationId,
  athenaQueries,
  wipConfig,
  basePropertyKey,
  inputDateType,
  presetVariables = [],
  validationVariables = [],
  variableSubTitle,
}: AthenaQueriesViewProps) => {
  const addQuery = (wc: WipConfig): void => {
    const queries = getIn(wc.formProps.values, basePropertyKey);
    finleyAssert(
      typeguards.creditAgreement.isAthenaConfigQueries(queries),
      `Retrieved non-Athena Queries structure for [key:${basePropertyKey}]`,
    );

    const queriesCopy = _.cloneDeep(queries);
    queriesCopy.push(createAthenaQueryShell(queriesCopy.length));
    wc.formProps.setFieldValue(basePropertyKey, queriesCopy);
  };

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Athena Queries
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add query"
            label="Add Query"
            onClick={() => addQuery(wipConfig)}
          />
        )}
      </Flex>
      {athenaQueries.length === 0 && (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no athena queries.
        </Text>
      )}
      {athenaQueries.map((query, idx) => (
        <AthenaQueryView
          organizationId={organizationId}
          key={`athena-query-view-${query.query.name}}`}
          wipConfig={wipConfig}
          basePropertyKey={basePropertyKey}
          fullPropertyKey={`${basePropertyKey}[${idx}]`}
          queryView={query}
          existingQueries={athenaQueries}
          idx={idx}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          validationVariables={validationVariables}
          variableSubTitle={variableSubTitle}
        />
      ))}
    </Box>
  );
};
